import React, { useState, useRef, useEffect, useCallback } from "react";

const fallbackNormalize = (file, resolve) => {
  const reader = new FileReader();
  reader.onload = (evt) => {
    const rawDataUrl = evt.target?.result;
    if (!rawDataUrl) return resolve(null);
    const img = new Image();
    img.onload = () => {
      const maxDim = 2560;
      let width = img.naturalWidth || img.width;
      let height = img.naturalHeight || img.height;
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.92));
    };
    img.onerror = () => resolve(rawDataUrl);
    img.src = rawDataUrl;
  };
  reader.readAsDataURL(file);
};

// Helper to normalize image EXIF orientation and clamp dimensions to 2560px max
const normalizeImageOrientationAndScale = (file) => {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && typeof window.createImageBitmap === "function") {
      window.createImageBitmap(file, { imageOrientation: "from-image" })
        .then((bmp) => {
          const maxDim = 2560;
          let width = bmp.width;
          let height = bmp.height;
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(bmp, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.92));
        })
        .catch(() => {
          fallbackNormalize(file, resolve);
        });
    } else {
      fallbackNormalize(file, resolve);
    }
  });
};

export default function PhotoSolutionModal({
  isOpen,
  onClose,
  onSubmitPhotos,
  isSubmitting = false
}) {
  const [activeTab, setActiveTab] = useState("camera"); // "camera" | "upload"
  const [pages, setPages] = useState([]); // [{ id, dataUrl, pageNumber, fileName }]
  const [previewPageId, setPreviewPageId] = useState(null);

  // Camera state
  const videoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [currentFacingMode, setCurrentFacingMode] = useState("environment");
  const [videoDevices, setVideoDevices] = useState([]);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);

  // File input refs
  const fileInputRef = useRef(null);
  const nativeCameraInputRef = useRef(null);

  // Stop camera helper
  const stopCamera = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
      });
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setIsCameraLoading(false);
    setCameraError("");
  }, []);

  // Start camera helper
  const startCamera = useCallback(async (facing = "environment", targetDeviceId = null) => {
    stopCamera();

    if (typeof window === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera is not supported on this browser.");
      return;
    }

    setIsCameraLoading(true);
    setCameraError("");

    try {
      const constraints = {
        video: targetDeviceId
          ? { deviceId: { exact: targetDeviceId } }
          : {
              facingMode: { ideal: facing },
              width: { ideal: 1920, min: 640 },
              height: { ideal: 1080, min: 480 }
            },
        audio: false
      };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing } },
          audio: false
        });
      }

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const inputs = devices.filter((d) => d.kind === "videoinput");
        if (inputs.length > 0) {
          setVideoDevices(inputs);
        }
      } catch (_) {}

      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", "true");
        videoRef.current.setAttribute("webkit-playsinline", "true");
        videoRef.current.muted = true;
        try {
          await videoRef.current.play();
        } catch (_) {}
      }

      setCameraActive(true);
      setIsCameraLoading(false);
      setCurrentFacingMode(facing);
    } catch (err) {
      console.warn("[PhotoSolutionModal] Camera error:", err);
      setIsCameraLoading(false);
      setCameraActive(false);
      let msg = "Could not access camera. Please allow camera permissions or use File Upload.";
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        msg = "Camera permission was denied. Please allow camera access in browser settings.";
      } else if (err.name === "NotFoundError") {
        msg = "No camera found on this device. Please use the Upload tab.";
      }
      setCameraError(msg);
    }
  }, [stopCamera]);

  // Flip camera between front & back
  const flipCamera = useCallback(async () => {
    if (videoDevices.length > 1) {
      const nextIdx = (currentDeviceIndex + 1) % videoDevices.length;
      setCurrentDeviceIndex(nextIdx);
      const targetDevice = videoDevices[nextIdx];
      const nextFacing = /front|user|face/i.test(targetDevice?.label) ? "user" : "environment";
      setCurrentFacingMode(nextFacing);
      await startCamera(nextFacing, targetDevice?.deviceId);
    } else {
      const nextFacing = currentFacingMode === "environment" ? "user" : "environment";
      setCurrentFacingMode(nextFacing);
      await startCamera(nextFacing);
    }
  }, [videoDevices, currentDeviceIndex, currentFacingMode, startCamera]);

  // Manage camera on modal open/tab change
  useEffect(() => {
    if (isOpen && activeTab === "camera") {
      startCamera(currentFacingMode);
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, activeTab, currentFacingMode, startCamera, stopCamera]);

  // Capture current video frame as photo
  const handleSnapPhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;

    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.92);

      const newPage = {
        id: `page_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        dataUrl,
        pageNumber: pages.length + 1,
        fileName: `notebook_page_${pages.length + 1}.jpg`
      };

      setPages((prev) => [...prev, newPage]);
      setPreviewPageId(newPage.id);
    } catch (err) {
      console.error("[PhotoSolutionModal] Capture error:", err);
    }
  };

  // Add files from file input
  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      if (!file.type.startsWith("image/")) continue;
      try {
        const dataUrl = await normalizeImageOrientationAndScale(file);
        if (dataUrl) {
          setPages((prev) => {
            const pageNum = prev.length + 1;
            const newPage = {
              id: `page_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 6)}`,
              dataUrl,
              pageNumber: pageNum,
              fileName: file.name || `page_${pageNum}.jpg`
            };
            return [...prev, newPage];
          });
        }
      } catch (err) {
        console.warn("[PhotoSolutionModal] Image normalization error:", err);
      }
    }

    if (e.target) e.target.value = "";
  };

  // Remove a page
  const handleDeletePage = (pageId, event) => {
    event?.stopPropagation();
    setPages((prev) => {
      const filtered = prev.filter((p) => p.id !== pageId);
      return filtered.map((p, idx) => ({ ...p, pageNumber: idx + 1 }));
    });
    if (previewPageId === pageId) {
      setPreviewPageId(null);
    }
  };

  // Submit all pages
  const handleSubmit = () => {
    if (pages.length === 0 || isSubmitting) return;
    onSubmitPhotos(pages);
  };

  if (!isOpen) return null;

  return (
    <div className="photo-modal-backdrop" onClick={onClose}>
      <div className="photo-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="photo-modal-header">
          <div className="photo-modal-title-group">
            <div className="photo-modal-badge">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 7h3l2-3h6l2 3h3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
                <circle cx="12" cy="14" r="4" />
              </svg>
              <span>Handwritten Solution Capture</span>
            </div>
            <h3 className="photo-modal-title">Snap or Upload Notebook Pages</h3>
            <p className="photo-modal-subtitle">
              Take photos of your handwritten notebook solution. Solutions spanning multiple pages are continuously read by AI.
            </p>
          </div>
          <button type="button" className="photo-modal-close-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {/* Tab switcher */}
        <div className="photo-modal-tabs">
          <button
            type="button"
            className={`photo-modal-tab ${activeTab === "camera" ? "active" : ""}`}
            onClick={() => setActiveTab("camera")}
          >
            📷 Camera
          </button>
          <button
            type="button"
            className={`photo-modal-tab ${activeTab === "upload" ? "active" : ""}`}
            onClick={() => setActiveTab("upload")}
          >
            📁 Upload Files
          </button>
        </div>

        {/* Tab Content */}
        <div className="photo-modal-body">
          {activeTab === "camera" ? (
            <div className="photo-modal-camera-pane">
              {cameraError ? (
                <div className="photo-camera-error">
                  <p>{cameraError}</p>
                  <div className="photo-camera-error-actions">
                    <button
                      type="button"
                      className="photo-btn-secondary"
                      onClick={() => nativeCameraInputRef.current?.click()}
                    >
                      Use Device Native Camera
                    </button>
                    <button
                      type="button"
                      className="photo-btn-secondary"
                      onClick={() => setActiveTab("upload")}
                    >
                      Switch to File Upload
                    </button>
                  </div>
                </div>
              ) : (
                <div className="photo-camera-viewport-wrap">
                  <video
                    ref={videoRef}
                    className="photo-camera-video"
                    autoPlay
                    playsInline
                    muted
                  />
                  {isCameraLoading && (
                    <div className="photo-camera-loading-overlay">
                      <div className="photo-spinner" />
                      <span>Starting camera...</span>
                    </div>
                  )}
                  {cameraActive && (
                    <div className="photo-camera-controls">
                      {videoDevices.length > 1 && (
                        <button
                          type="button"
                          className="photo-camera-flip-btn"
                          onClick={flipCamera}
                          title="Switch camera"
                        >
                          🔄 Switch
                        </button>
                      )}
                      <button
                        type="button"
                        className="photo-camera-snap-btn"
                        onClick={handleSnapPhoto}
                        title="Capture this page"
                      >
                        <div className="snap-btn-inner" />
                      </button>
                      <button
                        type="button"
                        className="photo-camera-native-btn"
                        onClick={() => nativeCameraInputRef.current?.click()}
                        title="Use system camera app"
                      >
                        📱 System Camera
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* Native camera file input fallback */}
              <input
                ref={nativeCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={handleFilesSelected}
              />
            </div>
          ) : (
            <div className="photo-modal-upload-pane">
              <div
                className="photo-upload-dropzone"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer?.files?.length) {
                    handleFilesSelected({ target: { files: e.dataTransfer.files } });
                  }
                }}
              >
                <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="#3b82f6" strokeWidth="1.8">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <div className="photo-upload-dropzone-text">
                  <strong>Click to select images</strong> or drag and drop here
                </div>
                <div className="photo-upload-dropzone-sub">
                  Supports multiple pages (PNG, JPG, JPEG, WEBP)
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleFilesSelected}
              />
            </div>
          )}

          {/* Captured Pages Strip */}
          <div className="photo-pages-section">
            <div className="photo-pages-header">
              <span className="photo-pages-count">
                Attached Pages ({pages.length})
              </span>
              {pages.length > 0 && (
                <button
                  type="button"
                  className="photo-pages-clear-all"
                  onClick={() => setPages([])}
                >
                  Clear all
                </button>
              )}
            </div>

            {pages.length === 0 ? (
              <div className="photo-pages-empty">
                No pages captured yet. Snap a photo or upload files to add Page 1.
              </div>
            ) : (
              <div className="photo-pages-strip">
                {pages.map((page, idx) => (
                  <div
                    key={page.id}
                    className={`photo-page-card ${previewPageId === page.id ? "active" : ""}`}
                    onClick={() => setPreviewPageId(page.id)}
                  >
                    <div className="photo-page-badge">Page {idx + 1}</div>
                    <img src={page.dataUrl} alt={`Page ${idx + 1}`} className="photo-page-thumb" />
                    <button
                      type="button"
                      className="photo-page-del-btn"
                      onClick={(e) => handleDeletePage(page.id, e)}
                      title="Remove page"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="photo-page-add-card"
                  onClick={() => {
                    if (activeTab === "camera") {
                      handleSnapPhoto();
                    } else {
                      fileInputRef.current?.click();
                    }
                  }}
                  title="Add another page"
                >
                  <span className="photo-page-add-icon">+</span>
                  <span>Add Page {pages.length + 1}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="photo-modal-footer">
          <div className="photo-modal-footer-info">
            {pages.length > 0 && (
              <span>
                ✓ {pages.length} page{pages.length === 1 ? "" : "s"} ready for Gemini Flash OCR & Pro grading
              </span>
            )}
          </div>
          <div className="photo-modal-footer-actions">
            <button
              type="button"
              className="photo-btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="photo-btn-submit"
              onClick={handleSubmit}
              disabled={pages.length === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="photo-spinner-inline" />
                  <span>Submitting & Grading...</span>
                </>
              ) : (
                `Submit Solution (${pages.length} Page${pages.length === 1 ? "" : "s"})`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

